import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiPlus, FiTrash2, FiEdit2, FiX, FiChevronDown, FiChevronUp, FiSearch,
  FiCheck, FiSlash, FiSend,
} from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useToast } from '../context/ToastContext';
import Modal from '../components/Modal/Modal';
import SearchSelect from '../components/SearchSelect/SearchSelect';
import p from './Pages.module.css';

const MM_BASE = '/v1/manual-market';
const MMO_BASE = '/v1/manual-market-option';
const INT_BASE = '/v1/clasico-integrations';

// ─────────────────────────────────────────────────────────────────────────────
// Option combobox with create-on-the-fly + per-option delete (×)
// ─────────────────────────────────────────────────────────────────────────────
function OptionCombobox({ options, value, onChange, onDeleteOption, onCreateOption, width = '100%' }) {
  // value: { id: number|null, code: string }
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const filtered = useMemo(() => {
    const sorted = [...options].sort((a, b) => a.code.localeCompare(b.code));
    if (!search) return sorted;
    const l = search.toLowerCase();
    return sorted.filter(o => o.code.toLowerCase().includes(l));
  }, [options, search]);

  const exactMatch = useMemo(
    () => options.find(o => o.code.toLowerCase() === search.toLowerCase().trim()),
    [options, search],
  );

  const pick = (opt) => {
    onChange({ id: opt.id, code: opt.code });
    setSearch('');
    setOpen(false);
  };

  const commitTyped = async () => {
    const code = search.trim();
    if (!code) return;
    if (exactMatch) {
      pick(exactMatch);
      return;
    }
    setCreating(true);
    try {
      const opt = await onCreateOption(code);
      if (opt && opt.id) {
        onChange({ id: opt.id, code: opt.code });
        setSearch('');
        setOpen(false);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = (e, opt) => {
    e.stopPropagation();
    onDeleteOption(opt);
  };

  const displayLabel = value?.code || '';

  return (
    <div ref={wrapRef} style={{ position: 'relative', width }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={p.formInput}
        style={{
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          width: '100%',
        }}
      >
        <span style={{ color: displayLabel ? 'var(--text-primary)' : 'var(--text-muted)' }}>
          {displayLabel || 'Select or type option…'}
        </span>
        {open ? <FiChevronUp /> : <FiChevronDown />}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 50,
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            maxHeight: 320,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ padding: 8, borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiSearch style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              ref={inputRef}
              className={p.formInput}
              placeholder="Search or type new code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitTyped(); } }}
              style={{ padding: '6px 10px', fontSize: '0.82rem' }}
            />
          </div>

          <ul style={{ listStyle: 'none', margin: 0, padding: 4, overflowY: 'auto', maxHeight: 220 }}>
            {filtered.length === 0 && !search.trim() && (
              <li style={{ padding: 10, color: 'var(--text-muted)', fontSize: '0.82rem' }}>No options yet.</li>
            )}
            {filtered.map(opt => (
              <li
                key={opt.id}
                onClick={() => pick(opt)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 10px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  background: value?.id === opt.id ? 'var(--bg-hover)' : 'transparent',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.background = value?.id === opt.id ? 'var(--bg-hover)' : 'transparent'}
              >
                <span style={{ color: 'var(--text-primary)' }}>{opt.code}</span>
                <button
                  type="button"
                  onClick={(e) => handleDelete(e, opt)}
                  title="Delete option permanently"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#e06c75',
                    cursor: 'pointer',
                    padding: 4,
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 4,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(224,108,117,0.15)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <FiX size={14} />
                </button>
              </li>
            ))}
          </ul>

          {search.trim() && !exactMatch && (
            <div
              onClick={creating ? undefined : commitTyped}
              style={{
                padding: '10px 12px',
                borderTop: '1px solid var(--border-color)',
                cursor: creating ? 'wait' : 'pointer',
                fontSize: '0.82rem',
                color: 'var(--accent)',
                fontWeight: 500,
                opacity: creating ? 0.6 : 1,
              }}
            >
              {creating ? 'Creating…' : `+ Create new option “${search.trim()}”`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Manual Market form (modal body)
// ─────────────────────────────────────────────────────────────────────────────
function emptyItem() {
  return { id: null, option: { id: null, code: '' }, odd: '', boostedOdd: '' };
}

function ManualMarketForm({
  initial,
  options,
  matches,
  fictiveMatches,
  integrations,
  onDeleteOption,
  onCreateOption,
  onSave,
  onCancel,
  saving,
  error,
}) {
  const [freeText, setFreeText] = useState(initial?.freeText || '');
  const [liability, setLiability] = useState(initial?.liability ?? '');
  const [threshold, setThreshold] = useState(initial?.threshold ?? '');
  const [maxPlayerBet, setMaxPlayerBet] = useState(initial?.maximumPlayerBet ?? '');
  const [oneBetPerPlayer, setOneBetPerPlayer] = useState(initial?.oneBetPerPlayer ?? true);
  const [integrationIds, setIntegrationIds] = useState(
    Array.isArray(initial?.integrationIds) ? [...initial.integrationIds] : [],
  );
  const [matchKind, setMatchKind] = useState(
    initial?.matchId ? 'real' : initial?.fictiveMatchId ? 'fictive' : 'none',
  );
  const [matchId, setMatchId] = useState(initial?.matchId ?? null);
  const [fictiveMatchId, setFictiveMatchId] = useState(initial?.fictiveMatchId ?? null);

  const [items, setItems] = useState(() => {
    if (initial?.items?.length) {
      return initial.items.map(it => ({
        id: it.id ?? null,
        option: { id: it.manualMarketOptionId, code: it.optionCode || '' },
        odd: it.odd ?? '',
        boostedOdd: it.boostedOdd ?? '',
      }));
    }
    return [emptyItem()];
  });

  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const canSubmit = freeText.trim() && items.length > 0
    && items.every(it => it.option.id && it.odd !== '' && !Number.isNaN(parseFloat(it.odd)))
    && (initial?.id ? true : integrationIds.length > 0); // on edit, allow 0 integrations (unpublish)

  const toggleIntegration = (id) => {
    setIntegrationIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload = {
      freeText: freeText.trim(),
      liability: liability === '' ? null : parseInt(liability, 10),
      threshold: threshold === '' ? null : parseInt(threshold, 10),
      maximumPlayerBet: maxPlayerBet === '' ? null : parseFloat(maxPlayerBet),
      oneBetPerPlayer,
      integrationIds: [...integrationIds],
      matchId: matchKind === 'real' ? matchId : null,
      fictiveMatchId: matchKind === 'fictive' ? fictiveMatchId : null,
    };
    onSave(payload, items);
  };

  // Implied probability sum (book percentage)
  // (removed: not part of backend contract)

  const matchOptions = useMemo(() => matches.map(m => {
    const home = m.teams?.find(t => (t.side || '').toLowerCase() === 'home')?.name;
    const away = m.teams?.find(t => (t.side || '').toLowerCase() === 'away')?.name;
    const label = home && away ? `${home} vs ${away}` : `Match #${m.id}`;
    return { value: m.id, label: `#${m.id} — ${label}` };
  }), [matches]);

  const fictiveOptions = useMemo(() => fictiveMatches.map(fm => {
    const home = fm.teams?.find(t => (t.side || '').toLowerCase() === 'home')?.name;
    const away = fm.teams?.find(t => (t.side || '').toLowerCase() === 'away')?.name;
    const label = home && away ? `${home} vs ${away}` : `Clasico #${fm.id}`;
    return { value: fm.id, label: `#${fm.id} — ${label}` };
  }), [fictiveMatches]);

  return (
    <>
      {error && <div style={{
        background: 'rgba(224,108,117,0.1)',
        border: '1px solid rgba(224,108,117,0.3)',
        borderRadius: 8, padding: '10px 14px', fontSize: '0.82rem',
        color: '#e06c75', marginBottom: 16,
      }}>{error}</div>}

      <div className={p.formGroup}>
        <label className={p.formLabel}>Free text *</label>
        <textarea
          className={p.formTextarea}
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="What is being bet on? (e.g. Will Messi score in the first half?)"
          maxLength={500}
        />
      </div>

      <div className={p.formRow}>
        <div className={p.formGroup}>
          <label className={p.formLabel}>Liability (optional)</label>
          <input
            className={p.formInput}
            type="number"
            value={liability}
            onChange={(e) => setLiability(e.target.value)}
            placeholder="e.g. 5000"
          />
        </div>
        <div className={p.formGroup}>
          <label className={p.formLabel}>Threshold % (optional)</label>
          <input
            className={p.formInput}
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            placeholder="e.g. 80"
          />
        </div>
      </div>

      <div className={p.formRow}>
        <div className={p.formGroup}>
          <label className={p.formLabel}>Max bet per player (optional)</label>
          <input
            className={p.formInput}
            type="number"
            step="0.01"
            value={maxPlayerBet}
            onChange={(e) => setMaxPlayerBet(e.target.value)}
            placeholder="e.g. 100"
          />
        </div>
        <div className={p.formGroup}>
          <label className={p.formLabel}>One bet per player</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={oneBetPerPlayer}
              onChange={(e) => setOneBetPerPlayer(e.target.checked)}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {oneBetPerPlayer ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
      </div>

      <div className={p.formGroup}>
        <label className={p.formLabel}>Linked match</label>
        <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
          {['none', 'real', 'fictive'].map(k => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.85rem' }}>
              <input
                type="radio"
                name="matchKind"
                checked={matchKind === k}
                onChange={() => {
                  setMatchKind(k);
                  if (k !== 'real') setMatchId(null);
                  if (k !== 'fictive') setFictiveMatchId(null);
                }}
              />
              {k === 'none' ? 'None' : k === 'real' ? 'Real match' : 'Fictive match (Clasico)'}
            </label>
          ))}
        </div>
        {matchKind === 'real' && (
          <SearchSelect
            options={matchOptions}
            value={matchId}
            onChange={setMatchId}
            placeholder="Select real match…"
            width="100%"
          />
        )}
        {matchKind === 'fictive' && (
          <SearchSelect
            options={fictiveOptions}
            value={fictiveMatchId}
            onChange={setFictiveMatchId}
            placeholder="Select fictive match…"
            width="100%"
          />
        )}
      </div>

      <div className={p.formGroup}>
        <label className={p.formLabel}>
          Available to integrations *{' '}
          <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
            (select one or more — only the chosen integrations can see this market and place bets)
          </span>
        </label>
        {integrations.length === 0 ? (
          <div style={{
            padding: '10px 14px', borderRadius: 8,
            border: '1px solid var(--border-color)',
            background: 'var(--bg-primary)',
            fontSize: '0.82rem', color: 'var(--text-muted)',
          }}>
            No integrations available. Create at least one integration first.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {integrations.map(intg => {
              const checked = integrationIds.includes(intg.id);
              return (
                <label
                  key={intg.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-color)'}`,
                    background: checked ? 'rgba(115,201,145,0.08)' : 'var(--bg-primary)',
                    cursor: 'pointer',
                    fontSize: '0.82rem',
                    color: 'var(--text-primary)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleIntegration(intg.id)}
                  />
                  <span>
                    <strong>{intg.name || intg.code || `#${intg.id}`}</strong>
                    {intg.code && intg.name && (
                      <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: '0.72rem' }}>
                        ({intg.code})
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}
        {integrationIds.length === 0 && integrations.length > 0 && (
          <div style={{
            marginTop: 6, fontSize: '0.75rem', color: '#e0a86c',
          }}>
            Select at least one integration — markets with no mapping are hidden from every integration.
          </div>
        )}
      </div>

      <div className={p.formGroup}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <label className={p.formLabel} style={{ margin: 0 }}>Outcomes (items) *</label>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 110px 110px 32px',
          gap: 8,
          marginBottom: 4,
          paddingLeft: 2,
        }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Option</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Odd</span>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Boosted odd</span>
          <span />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map((it, idx) => {
            return (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 110px 110px 32px',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <OptionCombobox
                  options={options}
                  value={it.option}
                  onChange={(opt) => updateItem(idx, { option: opt })}
                  onDeleteOption={onDeleteOption}
                  onCreateOption={onCreateOption}
                />
                <input
                  className={p.formInput}
                  type="number"
                  step="0.01"
                  min="1.01"
                  value={it.odd}
                  onChange={(e) => updateItem(idx, { odd: e.target.value })}
                  placeholder="Odd"
                />
                <input
                  className={p.formInput}
                  type="number"
                  step="0.01"
                  min="1.01"
                  value={it.boostedOdd}
                  onChange={(e) => updateItem(idx, { boostedOdd: e.target.value })}
                  placeholder="Boosted (opt)"
                  title="Boosted odd — optional promotional price shown to users"
                />
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  disabled={items.length === 1}
                  title="Remove row"
                  style={{
                    background: 'none',
                    border: '1px solid var(--border-color)',
                    color: items.length === 1 ? 'var(--text-muted)' : '#e06c75',
                    width: 32, height: 32, borderRadius: 6,
                    cursor: items.length === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={addItem}
          className={p.btnOutline}
          style={{ marginTop: 10 }}
        >
          <FiPlus /> Add outcome
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button type="button" className={p.btnDefault} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className={p.btnPrimary}
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
        >
          {saving ? 'Saving…' : (initial?.id ? 'Save changes' : 'Create market')}
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Confirm dialog (small)
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmDialog({ open, title, message, confirmLabel = 'Confirm', danger, onConfirm, onCancel, busy }) {
  return (
    <Modal open={open} onClose={busy ? undefined : onCancel} title={title}>
      <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', margin: '0 0 20px' }}>{message}</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className={p.btnDefault} onClick={onCancel} disabled={busy}>Cancel</button>
        <button
          className={danger ? p.btnDanger : p.btnPrimary}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function ManualMarkets() {
  const api = useApi();
  const toast = useToast();

  const [markets, setMarkets] = useState([]);
  const [options, setOptions] = useState([]);
  const [matches, setMatches] = useState([]);
  const [fictiveMatches, setFictiveMatches] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState(null); // null=closed, {} = new, {...market} = edit
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null); // { kind: 'market'|'option', id, label }
  const [confirmBusy, setConfirmBusy] = useState(false);

  const [settleTarget, setSettleTarget] = useState(null);   // market object
  const [settleOutcomes, setSettleOutcomes] = useState({}); // { itemId: 'won' | 'lost' }
  const [settleBusy, setSettleBusy] = useState(false);
  const [settleError, setSettleError] = useState(null);

  const [voidTarget, setVoidTarget] = useState(null); // market object
  const [voidReason, setVoidReason] = useState('');
  const [voidBusy, setVoidBusy] = useState(false);

  const [pushingIds, setPushingIds] = useState(new Set());

  const loadMarkets = useCallback(async () => {
    try {
      const r = await api.get(`${MM_BASE}/`);
      setMarkets(Array.isArray(r) ? r : (r?.items || []));
    } catch (e) {
      toast.error(`Failed to load markets: ${e.message}`);
    }
  }, [api, toast]);

  const loadOptions = useCallback(async () => {
    try {
      const r = await api.get(`${MMO_BASE}/`);
      setOptions(Array.isArray(r) ? r : []);
    } catch (e) {
      toast.error(`Failed to load options: ${e.message}`);
    }
  }, [api, toast]);

  const loadMatches = useCallback(async () => {
    try {
      const r = await api.get('/v1/match/?size=200');
      setMatches(r?.items || (Array.isArray(r) ? r : []));
    } catch { /* non-blocking */ }
  }, [api]);

  const loadFictiveMatches = useCallback(async () => {
    try {
      const r = await api.get('/v1/clasico/?size=200');
      setFictiveMatches(r?.items || (Array.isArray(r) ? r : []));
    } catch { /* non-blocking */ }
  }, [api]);

  const loadIntegrations = useCallback(async () => {
    try {
      const r = await api.get(`${INT_BASE}/`);
      setIntegrations(Array.isArray(r) ? r : (r?.items || []));
    } catch { /* non-blocking */ }
  }, [api]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      await Promise.all([loadMarkets(), loadOptions(), loadMatches(), loadFictiveMatches(), loadIntegrations()]);
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [loadMarkets, loadOptions, loadMatches, loadFictiveMatches, loadIntegrations]);

  const openCreate = () => { setFormError(null); setEditing({}); };
  const openEdit = (m) => { setFormError(null); setEditing(m); };
  const closeForm = () => { if (!saving) { setEditing(null); setFormError(null); } };

  const openSettle = (m) => { setSettleTarget(m); setSettleOutcomes({}); setSettleError(null); };
  const closeSettle = () => { if (!settleBusy) { setSettleTarget(null); setSettleError(null); } };

  const pickWinner = (itemId) => {
    setSettleOutcomes(prev => {
      // clicking WIN on the already-selected winner → remove winner (all lose)
      if (prev[itemId] === 'won') {
        const next = { ...prev };
        next[itemId] = 'lost';
        return next;
      }
      // mark this item won, every other item lost
      const next = {};
      (settleTarget?.items || []).forEach(it => {
        next[it.id] = it.id === itemId ? 'won' : 'lost';
      });
      return next;
    });
  };

  const pickLoser = (itemId) => {
    setSettleOutcomes(prev => {
      const next = { ...prev, [itemId]: 'lost' };
      return next;
    });
  };

  const openVoid = (m) => { setVoidTarget(m); setVoidReason(''); };
  const closeVoid = () => { if (!voidBusy) setVoidTarget(null); };

  // Save (create or update)
  const handleSave = async (payload, items) => {
    setSaving(true);
    setFormError(null);
    try {
      const resolvedItems = items.map(it => ({
        manualMarketOptionId: it.option.id,
        odd: parseFloat(it.odd),
        boostedOdd: it.boostedOdd !== '' && it.boostedOdd != null ? parseFloat(it.boostedOdd) : null,
      }));

      const body = { ...payload, items: resolvedItems };
      if (editing?.id) {
        await api.put(`${MM_BASE}/${editing.id}`, body);
        toast.success('Market updated');
      } else {
        await api.post(`${MM_BASE}/`, body);
        toast.success('Market created');
      }
      setEditing(null);
      loadMarkets();
    } catch (e) {
      setFormError(e.message || 'Failed to save market');
    } finally {
      setSaving(false);
    }
  };

  // Settle a market — derive winningItemId from per-item outcomes
  const handleSettle = async () => {
    if (!settleTarget) return;
    const items = settleTarget.items || [];
    const allDecided = items.every(it => settleOutcomes[it.id] != null);
    if (!allDecided) return;
    const winnerEntry = Object.entries(settleOutcomes).find(([, v]) => v === 'won');
    const winningItemId = winnerEntry ? parseInt(winnerEntry[0], 10) : null;
    setSettleBusy(true);
    setSettleError(null);
    try {
      const body = winningItemId != null ? { winningItemId } : {};
      await api.post(`${MM_BASE}/${settleTarget.id}/settle`, body);
      toast.success(winningItemId ? 'Market settled' : 'Market settled — all bets lost');
      setSettleTarget(null);
      loadMarkets();
    } catch (e) {
      setSettleError(e.message || 'Failed to settle market');
    } finally {
      setSettleBusy(false);
    }
  };

  // Void a market
  const handleVoid = async () => {
    if (!voidTarget) return;
    setVoidBusy(true);
    try {
      await api.post(`${MM_BASE}/${voidTarget.id}/void`, { reason: voidReason || undefined });
      toast.success('Market voided — all bets refunded');
      setVoidTarget(null);
      loadMarkets();
    } catch (e) {
      toast.error(`Void failed: ${e.message}`);
    } finally {
      setVoidBusy(false);
    }
  };

  // Push market to integrations (re-enqueue outbox event)
  const handlePush = async (market) => {
    setPushingIds(prev => new Set([...prev, market.id]));
    try {
      await api.post(`${MM_BASE}/${market.id}/push`, {});
      toast.success('Market queued for delivery to integrations');
    } catch (e) {
      toast.error(`Push failed: ${e.message}`);
    } finally {
      setPushingIds(prev => { const n = new Set(prev); n.delete(market.id); return n; });
    }
  };

  // Immediately create a new option (called from the combobox)
  const handleCreateOption = async (code) => {
    try {
      const opt = await api.post(`${MMO_BASE}/`, { code });
      setOptions(prev => {
        if (prev.some(o => o.id === opt.id)) return prev;
        return [...prev, opt];
      });
      return opt;
    } catch (e) {
      toast.error(`Failed to create option: ${e.message}`);
      return null;
    }
  };

  // Delete option (called from inside combobox)
  const requestDeleteOption = (opt) => {
    setConfirmDelete({
      kind: 'option',
      id: opt.id,
      label: opt.code,
    });
  };

  // Delete market
  const requestDeleteMarket = (m) => {
    setConfirmDelete({
      kind: 'market',
      id: m.id,
      label: m.freeText,
    });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    setConfirmBusy(true);
    try {
      if (confirmDelete.kind === 'option') {
        await api.del(`${MMO_BASE}/${confirmDelete.id}`);
        setOptions(prev => prev.filter(o => o.id !== confirmDelete.id));
        toast.success('Option deleted');
      } else {
        await api.del(`${MM_BASE}/${confirmDelete.id}`);
        setMarkets(prev => prev.filter(m => m.id !== confirmDelete.id));
        toast.success('Market deleted');
      }
      setConfirmDelete(null);
    } catch (e) {
      const msg = e.message || '';
      if (confirmDelete.kind === 'option' && /in use|409/i.test(msg)) {
        toast.error('This option is used by existing markets and cannot be deleted.');
      } else {
        toast.error(`Delete failed: ${msg}`);
      }
      setConfirmDelete(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return markets;
    const l = search.toLowerCase();
    return markets.filter(m => (m.freeText || '').toLowerCase().includes(l));
  }, [markets, search]);

  return (
    <div className={p.page}>
      <div className={p.pageHeader}>
        <h1 className={p.pageTitle}>Manual Markets</h1>
        <p className={p.pageSubtitle}>
          Free-form betting markets with custom outcomes — for real matches, fictive matches, or generic.
        </p>
      </div>

      <div className={p.toolbar}>
        <div className={p.toolbarFilters}>
          <input
            className={p.searchInput}
            placeholder="Search by free text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className={p.btnPrimary} onClick={openCreate}>
          <FiPlus /> New market
        </button>
      </div>

      {loading ? (
        <div className={p.emptyState}>
          <div className={p.emptyText}>Loading…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className={p.emptyState}>
          <div className={p.emptyText}>No manual markets yet.</div>
          <div className={p.emptySubtext}>Click “New market” to create the first one.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(m => (
            <MarketCard
              key={m.id}
              market={m}
              matches={matches}
              fictiveMatches={fictiveMatches}
              integrations={integrations}
              onEdit={() => openEdit(m)}
              onDelete={() => requestDeleteMarket(m)}
              onSettle={() => openSettle(m)}
              onVoid={() => openVoid(m)}
              onPush={() => handlePush(m)}
              pushing={pushingIds.has(m.id)}
            />
          ))}
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={closeForm}
        title={editing?.id ? `Edit market #${editing.id}` : 'New manual market'}
        wide
      >
        {editing && (
          <ManualMarketForm
            initial={editing.id ? editing : null}
            options={options}
            matches={matches}
            fictiveMatches={fictiveMatches}
            integrations={integrations}
            onDeleteOption={requestDeleteOption}
            onCreateOption={handleCreateOption}
            onSave={handleSave}
            onCancel={closeForm}
            saving={saving}
            error={formError}
          />
        )}
      </Modal>

      {/* ── Settle modal ── */}
      <Modal
        open={!!settleTarget}
        onClose={closeSettle}
        title={`Settle market #${settleTarget?.id}`}
      >
        {settleTarget && (() => {
          const items = settleTarget.items || [];
          const allDecided = items.every(it => settleOutcomes[it.id] != null);
          const hasWinner = Object.values(settleOutcomes).some(v => v === 'won');
          return (
            <>
              {settleError && (
                <div style={{
                  background: 'rgba(224,108,117,0.1)', border: '1px solid rgba(224,108,117,0.3)',
                  borderRadius: 8, padding: '10px 14px', fontSize: '0.82rem',
                  color: '#e06c75', marginBottom: 16,
                }}>{settleError}</div>
              )}
              <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', margin: '0 0 4px' }}>
                Mark each outcome for <strong>{settleTarget.freeText}</strong>:
              </p>
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 14px' }}>
                Click WIN on an outcome to set it as winner (all others become LOSE). Click WIN again to remove the winner.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {items.map(it => {
                  const outcome = settleOutcomes[it.id];
                  const isWon = outcome === 'won';
                  const isLost = outcome === 'lost';
                  return (
                    <div
                      key={it.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', borderRadius: 8,
                        border: `1px solid ${isWon ? 'rgba(115,201,145,0.5)' : isLost ? 'rgba(224,108,117,0.35)' : 'var(--border-color)'}`,
                        background: isWon ? 'rgba(115,201,145,0.08)' : isLost ? 'rgba(224,108,117,0.06)' : 'var(--bg-primary)',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9rem' }}>{it.optionCode}</span>
                        {it.boostedOdd != null ? (
                          <span style={{ marginLeft: 8, fontSize: '0.8rem' }}>
                            <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)' }}>{it.odd}</span>
                            {' '}
                            <span style={{ color: '#e0a86c', fontWeight: 600 }}>{it.boostedOdd} 🔥</span>
                          </span>
                        ) : (
                          <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--accent)' }}>@ {it.odd}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => pickWinner(it.id)}
                        style={{
                          padding: '5px 14px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600,
                          border: `1px solid ${isWon ? 'rgba(115,201,145,0.8)' : 'rgba(115,201,145,0.35)'}`,
                          background: isWon ? 'rgba(115,201,145,0.18)' : 'transparent',
                          color: isWon ? 'var(--accent)' : 'var(--text-muted)',
                          cursor: 'pointer',
                        }}
                      >
                        WIN
                      </button>
                      <button
                        type="button"
                        onClick={() => pickLoser(it.id)}
                        style={{
                          padding: '5px 14px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600,
                          border: `1px solid ${isLost ? 'rgba(224,108,117,0.8)' : 'rgba(224,108,117,0.25)'}`,
                          background: isLost ? 'rgba(224,108,117,0.14)' : 'transparent',
                          color: isLost ? '#e06c75' : 'var(--text-muted)',
                          cursor: 'pointer',
                        }}
                      >
                        LOSE
                      </button>
                    </div>
                  );
                })}
              </div>
              {allDecided && !hasWinner && (
                <div style={{
                  marginBottom: 14, padding: '8px 12px', borderRadius: 6,
                  background: 'rgba(224,168,108,0.1)', border: '1px solid rgba(224,168,108,0.3)',
                  fontSize: '0.8rem', color: '#e0a86c',
                }}>
                  No winner selected — all bets will be marked lost (payout = 0).
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button className={p.btnDefault} onClick={closeSettle} disabled={settleBusy}>Cancel</button>
                <button
                  className={p.btnPrimary}
                  onClick={handleSettle}
                  disabled={!allDecided || settleBusy}
                >
                  {settleBusy ? 'Settling…' : 'Confirm settlement'}
                </button>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* ── Void modal ── */}
      <Modal
        open={!!voidTarget}
        onClose={closeVoid}
        title={`Void market #${voidTarget?.id}`}
      >
        {voidTarget && (
          <>
            <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', margin: '0 0 14px' }}>
              Void <strong>{voidTarget.freeText}</strong>? All pending bets will be refunded.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: '0.82rem', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
                Reason (optional)
              </label>
              <input
                className={p.formInput}
                style={{ width: '100%' }}
                value={voidReason}
                onChange={e => setVoidReason(e.target.value)}
                placeholder="e.g. Match cancelled"
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className={p.btnDefault} onClick={closeVoid} disabled={voidBusy}>Cancel</button>
              <button className={p.btnDanger} onClick={handleVoid} disabled={voidBusy}>
                {voidBusy ? 'Voiding…' : 'Void market'}
              </button>
            </div>
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        title={confirmDelete?.kind === 'option' ? 'Delete option' : 'Delete market'}
        message={
          confirmDelete?.kind === 'option'
            ? `Delete option "${confirmDelete.label}" permanently? It will be removed from the system. This cannot be undone.`
            : `Delete market "${confirmDelete?.label}"? This will also remove all its outcomes. This cannot be undone.`
        }
        confirmLabel="Delete"
        danger
        onConfirm={handleConfirmDelete}
        onCancel={() => !confirmBusy && setConfirmDelete(null)}
        busy={confirmBusy}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Market card
// ─────────────────────────────────────────────────────────────────────────────
function MarketCard({ market, matches, fictiveMatches, integrations, onEdit, onDelete, onSettle, onVoid, onPush, pushing }) {
  const isOpen = !market.status || market.status === 'open';
  const isSettled = market.status === 'settled';
  const isVoided = market.status === 'void' || market.status === 'voided';
  const matchLabel = useMemo(() => {
    if (market.matchId) {
      const m = matches.find(x => x.id === market.matchId);
      if (!m) return `Match #${market.matchId}`;
      const home = m.teams?.find(t => (t.side || '').toLowerCase() === 'home')?.name;
      const away = m.teams?.find(t => (t.side || '').toLowerCase() === 'away')?.name;
      return `Match: ${home || '?'} vs ${away || '?'}`;
    }
    if (market.fictiveMatchId) {
      const fm = fictiveMatches.find(x => x.id === market.fictiveMatchId);
      if (!fm) return `Clasico #${market.fictiveMatchId}`;
      const home = fm.teams?.find(t => (t.side || '').toLowerCase() === 'home')?.name;
      const away = fm.teams?.find(t => (t.side || '').toLowerCase() === 'away')?.name;
      return `Clasico: ${home || '?'} vs ${away || '?'}`;
    }
    return 'Generic (no match)';
  }, [market, matches, fictiveMatches]);

  const mappedIntegrations = useMemo(() => {
    const ids = Array.isArray(market.integrationIds) ? market.integrationIds : [];
    return ids.map(id => integrations.find(i => i.id === id) || { id, name: `#${id}`, code: null });
  }, [market.integrationIds, integrations]);

  return (
    <div className={p.card} style={{ opacity: isVoided ? 0.75 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {market.freeText}
            </span>
            {isSettled && (
              <span style={{
                background: 'rgba(115,201,145,0.15)', border: '1px solid rgba(115,201,145,0.4)',
                color: 'var(--accent)', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
              }}>SETTLED</span>
            )}
            {isVoided && (
              <span style={{
                background: 'rgba(224,168,108,0.15)', border: '1px solid rgba(224,168,108,0.4)',
                color: '#e0a86c', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
              }}>VOIDED</span>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            <span>#{market.id}</span>
            <span>{matchLabel}</span>
            <span>{market.items?.length || 0} outcomes</span>
            {market.liability != null && <span>Liability: {market.liability}</span>}
            {market.threshold != null && <span>Threshold: {market.threshold}%</span>}
            {market.maximumPlayerBet != null && <span>Max/player: {market.maximumPlayerBet}</span>}
            {market.oneBetPerPlayer && <span>1 bet/player</span>}
            {isSettled && market.settledAt && (
              <span style={{ color: 'var(--accent)' }}>
                Settled: {new Date(market.settledAt).toLocaleDateString()}
              </span>
            )}
          </div>

          {market.items?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {market.items.map(it => {
                const isWinner = isSettled && market.winningItemId === it.id;
                return (
                  <span
                    key={it.id || `${it.manualMarketOptionId}-${it.odd}`}
                    style={{
                      background: isWinner ? 'rgba(115,201,145,0.12)' : 'var(--bg-primary)',
                      border: `1px solid ${isWinner ? 'rgba(115,201,145,0.6)' : 'var(--border-color)'}`,
                      borderRadius: 6,
                      padding: '4px 10px',
                      fontSize: '0.78rem',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {isWinner && <FiCheck size={11} style={{ color: 'var(--accent)', marginRight: 4, verticalAlign: 'middle' }} />}
                    <strong style={{ color: 'var(--text-primary)' }}>{it.optionCode}</strong>
                    {' @ '}
                    {it.boostedOdd != null ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', marginRight: 4 }}>{it.odd}</span>
                        <span style={{ color: '#e0a86c', fontWeight: 600 }}>{it.boostedOdd} 🔥</span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--accent)' }}>{it.odd}</span>
                    )}
                  </span>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginTop: 10 }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Integrations:
            </span>
            {mappedIntegrations.length === 0 ? (
              <span style={{
                background: 'rgba(224,168,108,0.1)',
                border: '1px solid rgba(224,168,108,0.4)',
                color: '#e0a86c',
                borderRadius: 6,
                padding: '3px 8px',
                fontSize: '0.72rem',
              }}>
                None — hidden from all integrations
              </span>
            ) : (
              mappedIntegrations.map(intg => (
                <span
                  key={intg.id}
                  style={{
                    background: 'rgba(115,201,145,0.10)',
                    border: '1px solid rgba(115,201,145,0.40)',
                    color: 'var(--accent)',
                    borderRadius: 6,
                    padding: '3px 8px',
                    fontSize: '0.72rem',
                  }}
                  title={intg.code || ''}
                >
                  {intg.name || intg.code || `#${intg.id}`}
                </span>
              ))
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
          {isOpen && (
            <>
              <button
                className={p.btnOutline}
                onClick={onSettle}
                title="Settle — pick winning outcome"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}
              >
                <FiCheck size={14} /> Settle
              </button>
              <button
                className={p.btnOutline}
                onClick={onVoid}
                title="Void — refund all bets"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: '#e0a86c', borderColor: 'rgba(224,168,108,0.4)' }}
              >
                <FiSlash size={14} /> Void
              </button>
              <button
                className={p.btnOutline}
                onClick={onPush}
                disabled={pushing}
                title="Push to integrations (re-enqueue outbox event)"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}
              >
                <FiSend size={14} /> {pushing ? '…' : 'Push'}
              </button>
            </>
          )}
          {isOpen && (
            <button className={p.btnOutline} onClick={onEdit} title="Edit" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
              <FiEdit2 size={14} /> Edit
            </button>
          )}
          <button className={p.btnDanger} onClick={onDelete} title="Delete" style={{ padding: '6px 12px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            <FiTrash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
